import { FileText, NotebookText } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { formatCurrency, formatDateTimeValue } from '@/lib/utils';

import { TablaDelTurno } from '../components/tabla-turno';
import type { ColumnaDelTurno } from '../components/tabla-turno';
import { turnoApi, useNotasDelTurno, useTurnoActual } from '../turno.api';
import type { DocumentoDelTurno } from '../turno.types';

/**
 * Notas de venta del turno (`/turno/notas`).
 *
 * Puerto de `Turno::notes`: las notas de venta que se emiten durante el turno del
 * operador, con su PDF (antes se imprimían con QZ Tray desde el navegador).
 */
export function TurnoNotasPage() {
  useDocumentTitle('Turno · Notas de venta');

  const turno = useTurnoActual();

  const [pagina, setPagina] = useState(1);
  const [buscar, setBuscar] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  const listado = useNotasDelTurno({ page: pagina, pageSize: 25, buscar });
  const filas = listado.data?.items ?? [];

  const verPdf = async (fila: DocumentoDelTurno) => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(turnoApi.ticketNota(fila.id), { nombre: `nota-venta-${fila.id}` });
    } catch (error) {
      setAviso(error instanceof Error ? error.message : 'No se pudo abrir la nota de venta.');
    }
  };

  const columnas: ColumnaDelTurno<DocumentoDelTurno>[] = [
    { encabezado: 'Fecha', celda: (fila) => formatDateTimeValue(fila.created_at) },
    {
      encabezado: 'Cliente',
      celda: (fila) => (
        <span className="block">
          <span className="block text-ink-800">{fila.cliente_nombre ?? '—'}</span>
          <span className="text-xs text-ink-400">{fila.cliente_numero ?? ''}</span>
        </span>
      ),
    },
    { encabezado: 'Placa', celda: (fila) => fila.cliente_placa ?? '—' },
    {
      encabezado: 'Nota',
      celda: (fila) => (
        <Badge variant="neutral">
          {fila.comprobante_serie ?? ''}-{fila.comprobante_numero ?? ''}
        </Badge>
      ),
    },
    {
      encabezado: 'Descuento',
      clase: 'text-right',
      celda: (fila) => formatCurrency(fila.descuento ?? 0),
    },
    {
      encabezado: 'Monto',
      clase: 'text-right',
      celda: (fila) => (
        <span className="font-medium text-ink-800">{formatCurrency(fila.monto_pagado ?? 0)}</span>
      ),
    },
    {
      encabezado: 'Acciones',
      celda: (fila) => (
        <Button
          variant="outline"
          size="sm"
          leftIcon={<FileText className="size-4" aria-hidden="true" />}
          onClick={() => void verPdf(fila)}
        >
          PDF
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Operador</p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <NotebookText className="size-6 text-brand-500" aria-hidden="true" />
          Notas de venta del turno
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-ink-500">
          Notas de venta emitidas en tu turno. El PDF es el mismo documento que se entrega al
          cliente.
        </p>
      </header>

      {aviso ? (
        <Alert variant="error" title="No se pudo abrir la nota de venta">
          {aviso}
        </Alert>
      ) : null}

      <TablaDelTurno
        titulo="Notas de venta"
        descripcion={
          turno.data?.abierto
            ? `Turno #${turno.data.turno?.id ?? ''} abierto.`
            : 'Cuando abras tu turno verás aquí sus notas de venta.'
        }
        columnas={columnas}
        filas={filas}
        total={listado.data?.total ?? 0}
        pagina={pagina}
        tamanioPagina={25}
        buscar={buscar}
        onBuscar={(valor) => {
          setBuscar(valor);
          setPagina(1);
        }}
        onPagina={setPagina}
        cargando={listado.isLoading}
        vacio="Todavía no hay notas de venta en este turno."
        sinTurno={turno.data ? !turno.data.abierto : false}
        avisoSinTurno="sus notas de venta"
      />
    </div>
  );
}
